import React from 'react';
import Box from '@mui/material/Box';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { dsV2 } from '../../ds-v2-tokens';

/**
 * Primitives de formulaire du Design System V2 (TEC-3051) — re-dérivées en MUI `sx` à partir des
 * classes de la maquette (`.fg`, `.fl`, `.fi`, `.fsel`, `.f2`, `.f-info`, `.f-hr`, `.switch-row`).
 * Source unique de vérité (coexistence DS V2, motif `ButtonV2`/`ModalV2`).
 */

export type FieldTone = 'expired' | 'warn' | 'valid';

const toneBorder = (tone?: FieldTone): string | undefined => {
  if (tone === 'expired') return dsV2.color.rougeBrd;
  if (tone === 'warn') return dsV2.color.amberBrd;
  if (tone === 'valid') return dsV2.color.vertBrd;
  return undefined;
};

/** Groupe libellé + champ (`.fg` + `.fl` + `.req` + `.fhint` / `.ferr`). */
export const FieldV2 = ({
  label,
  required,
  hint,
  error,
  children,
}: {
  label?: React.ReactNode;
  required?: boolean;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box sx={{ marginBottom: '14px' }}>
    {label ? (
      <Box
        component="label"
        sx={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          color: dsV2.color.t2,
          marginBottom: '4px',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
        {required ? (
          <Box component="span" sx={{ color: dsV2.color.rouge, marginLeft: '2px' }}>
            *
          </Box>
        ) : null}
      </Box>
    ) : null}
    {children}
    {hint && !error ? <Box sx={{ fontSize: '10.5px', color: dsV2.color.t4, marginTop: '4px' }}>{hint}</Box> : null}
    {error ? (
      <Box
        sx={{
          fontSize: '10.5px',
          color: dsV2.color.rouge,
          marginTop: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
        }}
      >
        <ErrorOutlineRoundedIcon sx={{ fontSize: 13 }} /> {error}
      </Box>
    ) : null}
  </Box>
);

const fieldBaseSx = (tone?: FieldTone) => ({
  width: '100%',
  padding: '9px 12px',
  backgroundColor: dsV2.color.blanc,
  border: `1px solid ${toneBorder(tone) ?? dsV2.color.border}`,
  borderRadius: dsV2.radius.sm,
  fontFamily: 'inherit',
  fontSize: 13,
  color: dsV2.color.noir,
  transition: 'border-color .15s, box-shadow .15s',
  outline: 'none',
  boxSizing: 'border-box' as const,
  '&:focus': { borderColor: dsV2.color.bleu, boxShadow: `0 0 0 3px ${dsV2.color.bleuDim}` },
  '&::placeholder': { color: dsV2.color.t4 },
  '&:disabled': { backgroundColor: dsV2.color.surface2, color: dsV2.color.t3, cursor: 'not-allowed' },
});

/** Champ texte/nombre natif stylé (`.fi`). */
export const InputV2 = ({
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled,
  readOnly,
  min,
  max,
  tone,
  ariaLabel,
}: {
  value: string | number;
  onChange?: (value: string) => void;
  type?: 'text' | 'number' | 'email' | 'tel' | 'date';
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  min?: number;
  max?: number;
  tone?: FieldTone;
  ariaLabel?: string;
}) => (
  <Box
    component="input"
    type={type}
    value={value ?? ''}
    placeholder={placeholder}
    disabled={disabled}
    readOnly={readOnly}
    min={min}
    max={max}
    aria-label={ariaLabel}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value)}
    sx={fieldBaseSx(tone)}
  />
);

/** Zone de texte native stylée (`.fi`, multi-lignes). */
export const TextareaV2 = ({
  value,
  onChange,
  rows = 3,
  maxLength,
  disabled,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange?: (value: string) => void;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
}) => (
  <Box
    component="textarea"
    rows={rows}
    value={value ?? ''}
    maxLength={maxLength}
    disabled={disabled}
    placeholder={placeholder}
    aria-label={ariaLabel}
    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange?.(e.target.value)}
    sx={{ ...fieldBaseSx(), resize: 'vertical', lineHeight: 1.5, fontFamily: dsV2.font.sans }}
  />
);

const SELECT_ARROW =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23B8B4AB'/%3E%3C/svg%3E\")";

export type SelectV2Option = { value: string | number; label: string; disabled?: boolean };

/** Liste déroulante native stylée (`.fsel`). */
export const SelectV2 = ({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  ariaLabel,
}: {
  value: string | number;
  onChange?: (value: string) => void;
  options: SelectV2Option[];
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
}) => (
  <Box
    component="select"
    value={value ?? ''}
    disabled={disabled}
    aria-label={ariaLabel}
    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange?.(e.target.value)}
    sx={{
      ...fieldBaseSx(),
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: SELECT_ARROW,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 12px center',
      paddingRight: '30px',
    }}
  >
    {placeholder ? <option value="">{placeholder}</option> : null}
    {options.map((o) => (
      <option key={o.value} value={o.value} disabled={o.disabled}>
        {o.label}
      </option>
    ))}
  </Box>
);

/** Grille deux colonnes (`.f2`). */
export const Row2 = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '10px' }}>{children}</Box>
);

export type InfoTone = 'blue' | 'amber';

/** Encart d'information contextuel (`.f-info` bleu / ambre). */
export const InfoV2 = ({
  tone = 'blue',
  icon,
  children,
}: {
  tone?: InfoTone;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const palette =
    tone === 'amber'
      ? { bg: dsV2.color.amberDim, color: dsV2.color.amberDark, border: dsV2.color.amberBrd, icon: dsV2.color.amber }
      : { bg: dsV2.color.bleuDim, color: dsV2.color.bleuDark, border: dsV2.color.bleuBrd, icon: dsV2.color.bleu };
  return (
    <Box
      sx={{
        padding: '10px 12px',
        borderRadius: dsV2.radius.sm,
        fontSize: 12,
        marginBottom: '14px',
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-start',
        lineHeight: 1.5,
        background: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.border}`,
      }}
    >
      {icon ? <Box sx={{ flexShrink: 0, marginTop: '1px', color: palette.icon, display: 'flex' }}>{icon}</Box> : null}
      <Box>{children}</Box>
    </Box>
  );
};

/** Filet de séparation (`.f-hr`). */
export const DividerV2 = () => (
  <Box component="hr" sx={{ border: 'none', borderTop: `1px solid ${dsV2.color.borderLight}`, margin: '16px 0' }} />
);

/** Ligne case à cocher + libellé (`.switch-row`). */
export const CheckRow = ({
  checked,
  onChange,
  children,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
  disabled?: boolean;
}) => (
  <Box
    component="label"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '12.5px',
      color: dsV2.color.t2,
      marginBottom: '12px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <Box
      component="input"
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
      sx={{ width: 17, height: 17, accentColor: dsV2.color.bleu, flexShrink: 0, margin: 0 }}
    />
    <span>{children}</span>
  </Box>
);
